import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  buildAppliesFilterParams,
  getFilterSections,
  getValueToLabel,
} from "./appliesFilterHelpers";
import {
  DATE_RANGE_PICKER_CONFIG,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  TABLE_TOOLBAR_CONFIG,
  getAppliesFilterKeys,
} from "./AppliesPageConfig";
import { formatApplicationSourceTypes } from "../../utils/applicationSourceType";

import type { FilterSectionConfig } from "@/components/ui/v2/filters/FiltersDrawer";
import type { ActiveFilter } from "@/components/ui/v2/table/table-active-filters";
import { URL_PARAMS } from "@/constants/urlParams";
import {
  useAppliesMetrics,
  useApplyFilters,
  useApplies,
  type UseApplyFiltersParams,
} from "@/features/integrations/modules/applies/api";
import type { RenderContext } from "@/features/integrations/shared/renderer/types";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import type { ApplyType } from "@/types/apply/ApplyType";
import {
  formatLocalDateOnly,
  localStartOfDayUtc,
  localEndOfDayUtc,
  parseLocalDateOnly,
} from "@/utils/dateUtils";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type UseAppliesPageOptions = {
  clientId?: string;
};

export type UseAppliesPageResult = {
  context: RenderContext;
  clientScoped: boolean;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  filterSections: FilterSectionConfig[];
  activeFilters: ActiveFilter[];
  removeFilter: (f: ActiveFilter) => void;
  clearFilters: () => void;
  filtersLoading: boolean;
  totalRecords: number;
  /** Everything a preview count query needs except the pending filter selection itself. */
  countBaseParams: {
    clientId?: string;
    tenantId: string | null;
    search?: string;
    startDate: string;
    endDate: string;
  };
  filterKeys: readonly string[];
  /** Scope for the drawer's paged option queries — the same one its option list is drawn from. */
  filterOptionParams: UseApplyFiltersParams;
  totalDisplay: string;
  countTotal: number;
  distinctJobBoards: number;
  distinctClients: number;
  metricsLoading: boolean;
  jobBoardChartData: { label: string; value: number }[];
  chartLoading: boolean;
  searchParams: ReturnType<typeof useSearchParams>[0];
};

export function useAppliesPage(
  options?: UseAppliesPageOptions,
): UseAppliesPageResult {
  const clientId = options?.clientId;
  const { context: authContext } = useAuth();
  // Client-context users (Managers, Spectators) are always client-scoped,
  // even without a clientId in the URL — the EF global query filter restricts their data.
  const clientScoped = !!clientId || authContext === "client";
  const filterKeys = getAppliesFilterKeys(clientScoped);
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const { orderBy: rawOrderBy, descending: rawDescending } =
    useSortSearchParams();
  const orderBy = rawOrderBy ?? "dateSubmitted";
  const descending = rawDescending ?? true;
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const { searchValue, debouncedSearchValue, setSearchValue } =
    useSearchSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Reset filters, search, dates, and pagination when tenant switches — none of these carry
  // over between tenants. We also reset the search hook's local state since it initializes
  // from the URL only once at mount.
  const [prevTenantId, setPrevTenantId] = useState(tenantId);
  useEffect(() => {
    if (prevTenantId === tenantId) return;
    setPrevTenantId(tenantId);
    // Skip the null → value transition on initial auth hydration — that's not a user-triggered switch.
    if (prevTenantId == null || tenantId == null) return;
    const next = new URLSearchParams(searchParams);
    filterKeys.forEach((k) => next.delete(k));
    next.delete(URL_PARAMS.SEARCH);
    next.delete("startDate");
    next.delete("endDate");
    next.set(URL_PARAMS.PAGE, "1");
    setSearchParams(next, { replace: true });
    setSearchValue("");
  }, [tenantId, prevTenantId, searchParams, setSearchParams, filterKeys, setSearchValue]);

  const startDate = searchParams.get("startDate")?.trim() || undefined;
  const endDate = searchParams.get("endDate")?.trim() || undefined;

  useEffect(() => {
    if (!searchParams.get("startDate") && !searchParams.get("endDate")) {
      const next = new URLSearchParams(searchParams);
      next.set("startDate", formatLocalDateOnly(DEFAULT_START_DATE));
      next.set("endDate", formatLocalDateOnly(DEFAULT_END_DATE));
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Centralized filter params — single source of truth for all hooks
  const filterParams = useMemo(() => {
    const params = buildAppliesFilterParams(searchParams, filterKeys);
    // Client-scoped page: lock clients to the single client from the URL
    if (clientId) params.clients = [clientId];
    return params;
  }, [searchParams, filterKeys, clientId]);

  // URL stores local date strings (YYYY-MM-DD) for display.
  // API needs UTC ISO strings so filters match UTC-stored CreatedDate values.
  const effectiveStartLocal =
    parseLocalDateOnly(startDate) ?? DEFAULT_START_DATE;
  const effectiveEndLocal = parseLocalDateOnly(endDate) ?? DEFAULT_END_DATE;
  const effectiveStartDate = localStartOfDayUtc(effectiveStartLocal);
  const effectiveEndDate = localEndOfDayUtc(effectiveEndLocal);

  // --- Data hooks (all share the same filterParams) ---

  const {
    data: applies,
    isLoading,
    isFetching,
  } = useApplies({
    ...filterParams,
    page,
    pageSize,
    search: debouncedSearchValue ?? undefined,
    tenantId,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    sortDirection: descending ? "desc" : "asc",
    sortColumn: orderBy,
  });

  // Filter counts only depend on date range + tenant — not on active filter selections.
  // This matches the reporting page pattern: counts show all available options, not cascaded.
  // The drawer's paged dimensions load on scroll from the same scope, so it is shared with them.
  const filterOptionParams = useMemo(
    () => ({
      clients: clientId ? [clientId] : undefined,
      search: debouncedSearchValue ?? undefined,
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
      tenantId,
    }),
    [clientId, debouncedSearchValue, effectiveStartDate, effectiveEndDate, tenantId],
  );

  const { data: filterData, isLoading: filtersLoading } =
    useApplyFilters(filterOptionParams);

  // Filtered counts for the chart — updates when active filters change.
  const { data: filteredCounts, isLoading: chartLoading } = useApplyFilters({
    ...filterParams,
    search: debouncedSearchValue ?? undefined,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    tenantId,
  });

  const countBaseParams = useMemo(
    () => ({
      clientId,
      tenantId,
      search: debouncedSearchValue ?? undefined,
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
    }),
    [clientId, tenantId, debouncedSearchValue, effectiveStartDate, effectiveEndDate],
  );

  const {
    data: metrics,
    isLoading: metricsLoading,
    isFetching: metricsFetching,
  } = useAppliesMetrics({
    ...filterParams,
    search: debouncedSearchValue ?? undefined,
    tenantId,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
  });

  const handleSort = useTableSortHandler<ApplyType>();

  const onRowClick = useCallback(
    (row: unknown) => {
      const applyRow = row as { id?: string };
      if (applyRow.id) navigate(applyRow.id);
    },
    [navigate],
  );

  // --- URL param helpers ---

  const updateParams = useCallback(
    (opts: { deleteKeys?: string[]; set?: Record<string, string[]> }) => {
      const next = new URLSearchParams(searchParams);
      opts.deleteKeys?.forEach((k) => next.delete(k));
      if (opts.set) {
        Object.entries(opts.set).forEach(([k, vals]) => {
          next.delete(k);
          vals.forEach((v) => next.append(k, v));
        });
      }
      next.set(URL_PARAMS.PAGE, "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setDateRange = useCallback(
    (from?: Date, to?: Date) => {
      updateParams({
        set: {
          startDate: from ? [formatLocalDateOnly(from)] : [],
          endDate: to ? [formatLocalDateOnly(to)] : [],
        },
      });
    },
    [updateParams],
  );

  const onFilterChange = useCallback(
    (key: string, value: unknown) => {
      if (
        key !== TABLE_TOOLBAR_CONFIG.dateRangeFilterKey ||
        value == null ||
        typeof value !== "object"
      )
        return;
      const range = value as { startDate?: string; endDate?: string };
      const from = range.startDate ? new Date(range.startDate) : undefined;
      const to = range.endDate ? new Date(range.endDate) : undefined;
      setDateRange(from, to);
    },
    [setDateRange],
  );

  const filterValues = useMemo(
    () => ({
      [TABLE_TOOLBAR_CONFIG.dateRangeFilterKey]: {
        startDate:
          searchParams.get("startDate") ??
          formatLocalDateOnly(DEFAULT_START_DATE),
        endDate:
          searchParams.get("endDate") ?? formatLocalDateOnly(DEFAULT_END_DATE),
      },
    }),
    [searchParams],
  );

  // --- Derived state ---

  const filterSections = useMemo(
    () => getFilterSections(filterData, filterKeys, t),
    [filterData, filterKeys, t],
  );
  const valueToLabel = useMemo(
    () => getValueToLabel(filterData, filterKeys, t),
    [filterData, filterKeys, t],
  );
  // Integrations arrive complete (bounded by the job board catalogue), so the chart sees every bar.
  const jobBoardChartData = useMemo(
    () =>
      (filteredCounts?.integration ?? []).map((o) => ({
        label: t(o.label, { defaultValue: o.label }),
        value: o.count,
      })),
    [filteredCounts, t],
  );

  const activeFilters = useMemo((): ActiveFilter[] => {
    return filterKeys.flatMap((key) =>
      searchParams.getAll(key).map((v) => ({
        key,
        label: t(`common.${key}`),
        value: v,
        valueLabel: valueToLabel[key]?.[v] ?? v,
      })),
    );
  }, [searchParams, t, valueToLabel, filterKeys]);

  const removeFilter = useCallback(
    (f: ActiveFilter) => {
      const current = searchParams
        .getAll(f.key)
        .filter((v) => v !== String(f.value));
      updateParams({ set: { [f.key]: current } });
    },
    [searchParams, updateParams],
  );

  const clearFilters = useCallback(() => {
    updateParams({ deleteKeys: [...filterKeys] });
  }, [updateParams, filterKeys]);

  // totalRecords from the list endpoint is the source of truth for the filtered count
  const totalRecords = applies?.totalRecords ?? 0;
  const hasFilters =
    !!searchValue ||
    filterKeys.some((key) => searchParams.getAll(key).length > 0);
  const countTotal = totalRecords;
  const distinctJobBoards = metrics?.distinctIntegrations ?? 0;
  const distinctClients = metrics?.distinctClients ?? 0;
  const totalDisplay =
    isLoading || isFetching
      ? t("common.loading", "Loading...")
      : String(countTotal);

  // Flatten client.name into clientName for column accessor compatibility. The jobSourceUrl deep link
  // is built server-side and arrives on each row; the Link column renders it (null → plain text).
  const tableData = useMemo(
    () =>
      (applies?.data ?? []).map((row) => ({
        ...row,
        clientName: row.client?.name ?? "",
        jobBoardName: row.jobBoardName
          ? t(row.jobBoardName, { defaultValue: row.jobBoardName })
          : "",
        applicationSourceTypes: formatApplicationSourceTypes(row.applicationSourceTypes, t),
      })),
    [applies?.data, t],
  );

  const context = useMemo<RenderContext>(
    () => ({
      integrationName: "applies",
      moduleName: "applies",
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      executeAction: async () => {},
      tableData,
      isLoading: isLoading || isFetching,
      pagination: {
        page,
        pageSize,
        totalRecords,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      },
      // Pass the raw (URL-derived) sort state to the table so the column header only
      // shows a sort icon after the user explicitly clicks one. The API call above
      // continues to use the effective orderBy/descending (with the dateSubmitted-desc default).
      orderBy: rawOrderBy,
      descending: rawDescending,
      onSort: handleSort as RenderContext["onSort"],
      onRowClick,
      emptyMessage: hasFilters
        ? t("common.noResultsFound")
        : t("integrations.cockpit.applies.noApplies"),
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      filterValues,
      onFilterChange,
      dateRangePickerOptions: {
        maxDate: new Date(),
        ...DATE_RANGE_PICKER_CONFIG,
      },
    }),
    [
      clientId,
      tenantId,
      tableData,
      isLoading,
      isFetching,
      page,
      pageSize,
      totalRecords,
      setPage,
      setPageSize,
      rawOrderBy,
      rawDescending,
      handleSort,
      onRowClick,
      hasFilters,
      t,
      searchValue,
      setSearchValue,
      filterValues,
      onFilterChange,
    ],
  );

  return {
    context,
    clientScoped,
    filtersOpen,
    setFiltersOpen,
    filterSections,
    activeFilters,
    removeFilter,
    clearFilters,
    filtersLoading,
    totalRecords,
    countBaseParams,
    filterKeys,
    filterOptionParams,
    totalDisplay,
    countTotal,
    distinctJobBoards,
    distinctClients,
    metricsLoading: metricsLoading || metricsFetching,
    jobBoardChartData,
    chartLoading,
    searchParams,
  };
}
